import { FormHelperText } from '@mui/material'
import BaseInput, { BaseInputProps } from '@src/common/components/BaseInput'
import { extractErrorStatus } from '@src/common/forms'
import { RequiredField } from '@src/common/utils'
import React, { ReactChild, ReactFragment, ReactPortal } from 'react'
import { FieldPathValue, FieldValues, Path, useController, UseControllerProps } from 'react-hook-form'

type SetValueAsType<T extends FieldValues, V extends Path<T>> = (
  value: string,
  event: OnChangeEventType,
) => FieldPathValue<T, V> | undefined

type SelectComputingType<T extends FieldValues, V extends Path<T>> = {
  computeValue: (value: FieldPathValue<T, V> | undefined) => BaseInputProps['value']
  setValueAs: SetValueAsType<T, V>
}

type FormProps<T extends FieldValues, V extends Path<T>> = RequiredField<UseControllerProps<T, V>, 'control'> &
  (
    | ({
        select: true
        children: ReactChild | ReactFragment | ReactPortal
      } & FieldPathValue<T, V> extends BaseInputProps['value']
        ? Partial<SelectComputingType<T, V>>
        : SelectComputingType<T, V>)
    | never
  )

type CustomProps<T extends FieldValues, V extends Path<T>> = {
  setValueAs?: SetValueAsType<T, V>
  helperText?: string
}

type Props<T extends FieldValues, V extends Path<T>> = FormProps<T, V> & BaseInputProps & CustomProps<T, V>

type OnChangeEventType = Parameters<Required<BaseInputProps>['onChange']>[0]

const FormField = <T extends FieldValues, V extends Path<T>>(props: Props<T, V>) => {
  const { control, defaultValue, name, rules, shouldUnregister, setValueAs, helperText, computeValue, ...rest } = props

  const { field, fieldState } = useController({
    control,
    defaultValue,
    name,
    rules,
    shouldUnregister,
  })
  const { error, isInvalid } = extractErrorStatus(fieldState)

  const onChange = (event: OnChangeEventType) => {
    if (setValueAs) {
      field.onChange(setValueAs(event.target.value, event))
      return
    }
    field.onChange(event)
  }

  const value = computeValue ? computeValue(field.value) : field.value

  return (
    <div>
      <BaseInput error={isInvalid} {...rest} {...field} onChange={onChange} value={value ?? ''}>
        {props.children}
      </BaseInput>
      {error ? (
        <FormHelperText error={true}>{error}</FormHelperText>
      ) : helperText ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : null}
    </div>
  )
}

export default FormField
